import { useEffect, useRef, useState } from "react";
import { Button, ButtonProps } from "react-bootstrap";
import { DashCircle, Pen } from "react-bootstrap-icons";
import { valueFix } from "../utility/valueFix";
import { ControlBase, ControlBaseProps } from "./ControlBase";

/**
 * Properties for a compact text control with an explicit edit mode and optional deletion.
 */
export interface ControlInlineEditProps extends ControlBaseProps {
  id: string;
  deleteLabel?: string;
  draftValue?: string;
  editButtonVariant?: ButtonProps["variant"];
  editLabel?: string;
  editOnDoubleClick?: boolean;
  onDelete?: () => void;
  onDraftChange?: (value: string) => void;
  onEditChange?: (editing: boolean) => void;
  preventInvalidExit?: boolean;
  ReadComponent?: React.FC<{ value: string }>;
  showEditButtonWhileEditing?: boolean;
}

/**
 * Manages inline draft edits, keyboard commit/cancel and focus without replacing the base input.
 * Consumers supply validation through isInvalid and receive draft changes independently of commits.
 *
 * @param props - Input properties, edit callbacks and optional delete action
 * @param props.id - Input identifier used by labels and edit-mode focus
 * @param props.deleteLabel - Accessible name and tooltip for the optional delete button
 * @param props.draftValue - Optional externally supplied draft, such as a dropdown selection
 * @param props.editButtonVariant - Bootstrap appearance of the pen button
 * @param props.editLabel - Accessible name and tooltip for the pen button
 * @param props.editOnDoubleClick - Allows double-clicking the read display to enter edit mode
 * @param props.disabled - Prevents editing and deletion when the consumer is read-only
 * @param props.isInvalid - Whether the current draft has failed or awaits validation
 * @param props.onDelete - Optional action for deleting the item represented by the control
 * @param props.onDraftChange - Receives each typed draft before it is committed
 * @param props.onEditChange - Reports entering and leaving edit mode
 * @param props.onInput - Optional input event callback forwarded after draft tracking
 * @param props.preventInvalidExit - Keeps invalid drafts focused, including on Escape
 * @param props.ReadComponent - Optional custom display used outside editable input mode
 * @param props.setValue - Receives the committed draft after Enter or blur
 * @param props.showEditButtonWhileEditing - Keeps a disabled pen visible while editing
 * @param props.value - Committed text displayed outside edit mode
 * @param props.whiteSpaceFix - Normalises whitespace before committing a draft
 * @param props.rest - Remaining ControlBase properties for input appearance and behaviour
 * @returns The input with inline edit and optional delete buttons
 */
export const ControlInlineEdit = ({
  id,
  deleteLabel = "Delete",
  draftValue,
  editButtonVariant = "link",
  editLabel = "Edit",
  editOnDoubleClick = false,
  disabled = false,
  isInvalid = false,
  onDelete,
  onDraftChange,
  onEditChange,
  onInput,
  preventInvalidExit = true,
  ReadComponent,
  setValue,
  showEditButtonWhileEditing = false,
  value,
  whiteSpaceFix = false,
  ...rest
}: ControlInlineEditProps): React.ReactElement => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const cancelled = useRef(false);
  const inputDisabled = !editing || disabled || !setValue;

  useEffect(() => {
    if (editing) {
      const input = document.getElementById(id);
      if (input) {
        input.focus();
      }
    }
  }, [editing, id]);

  /**
   * Captures the committed value as a draft and enters inline edit mode.
   */
  const startEdit = (): void => {
    cancelled.current = false;
    setDraft(value);
    setEditing(true);
    onDraftChange?.(value);
    onEditChange?.(true);
  };

  /**
   * Commits or cancels an edit when focus leaves the control, retaining invalid drafts.
   *
   * @param event - Wrapper blur containing the input and the next focused element
   */
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (editing && !event.currentTarget.contains(event.relatedTarget)) {
      const input = event.target as HTMLInputElement | HTMLTextAreaElement;
      if (preventInvalidExit && isInvalid) {
        queueMicrotask(() => input.focus());
      } else {
        if (!cancelled.current && !disabled) {
          setValue?.(whiteSpaceFix ? valueFix(input.value, true) : input.value);
        }
        cancelled.current = false;
        setDraft(value);
        setEditing(false);
        onEditChange?.(false);
      }
    }
  };

  return (
    <div
      className="d-flex align-items-start gap-2 w-100"
      onBlurCapture={handleBlur}
    >
      <div
        className="flex-grow-1"
        style={{ minWidth: 0 }}
        onDoubleClick={
          editOnDoubleClick && !disabled && setValue && !editing ? startEdit : undefined
        }
      >
        {inputDisabled && ReadComponent ? (
          <ReadComponent value={value} />
        ) : (
          <ControlBase
            {...rest}
            id={id}
            disabled={inputDisabled}
            isInvalid={isInvalid}
            value={editing ? (draftValue ?? draft) : value}
            setValue={setDraft}
            whiteSpaceFix={whiteSpaceFix}
            debounceMilliseconds={-1}
            onInput={(event) => {
              setDraft(event.currentTarget.value);
              onDraftChange?.(event.currentTarget.value);
              onInput?.(event);
            }}
            onKeyDownCapture={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                if (!preventInvalidExit || !isInvalid) {
                  cancelled.current = true;
                  event.currentTarget.blur();
                  setDraft(value);
                }
              } else if (
                event.key === "Enter" &&
                !(event.shiftKey && rest.allowCarriageReturn && rest.type === "textarea")
              ) {
                event.preventDefault();
                event.stopPropagation();
                event.currentTarget.blur();
              }
            }}
          />
        )}
      </div>
      {setValue && (!editing || showEditButtonWhileEditing) && (
        <Button
          size="sm"
          variant={editButtonVariant}
          className={editButtonVariant === "link" ? "p-0 text-primary" : undefined}
          aria-label={editLabel}
          title={editLabel}
          disabled={disabled || editing}
          style={{ flexShrink: 0 }}
          onClick={startEdit}
        >
          <Pen />
        </Button>
      )}
      {onDelete && (
        <Button
          size="sm"
          variant="link"
          className="p-0 text-danger"
          aria-label={deleteLabel}
          title={deleteLabel}
          disabled={disabled || editing}
          style={{ flexShrink: 0 }}
          onClick={() => onDelete()}
        >
          <DashCircle />
        </Button>
      )}
    </div>
  );
};

ControlInlineEdit.displayName = "ControlInlineEdit";
